import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cake, Gift } from "lucide-react";
import api from "@/lib/api";

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const role = (desc) => { const r = (desc || "").split(/[.;(]/)[0].trim(); return /\bwas born\b/i.test(r) && r.length < 40 ? "" : r.slice(0, 60); };

const Person = ({ p, today, testid }) => (
  <li data-testid={testid} className="flex items-start gap-3 bg-white border border-[#e5e7eb] rounded-xl px-4 py-3">
    {p.portrait ? (
      <img src={p.portrait} alt={p.name} loading="lazy" data-testid={`${testid}-portrait`} className="shrink-0 w-12 h-12 rounded-full object-cover object-top border border-[#e5e7eb] bg-paper" />
    ) : (
      <span className="shrink-0 w-12 h-12 rounded-full bg-ink text-gold flex items-center justify-center text-[13px] font-bold tracking-wide">{initials(p.name)}</span>
    )}
    <span className="min-w-0 flex-1">
      <span className="block text-[14px] font-semibold text-ink truncate">{p.name}</span>
      <span className="block text-[12px] text-ink-muted truncate">{p.wiki_description || role(p.description) || "Born " + p.year}</span>
      <span className="block mt-0.5 text-[11px] font-semibold text-gold">{today ? `Born ${p.year} · ${p.age} years ago today` : `${p.date_label} · born ${p.year}`}</span>
    </span>
  </li>
);

export const FamousBirthdays = ({ ddmm, hideToday = false }) => {
  const [data, setData] = useState(null);
  const isToday = !ddmm || ddmm === `${String(new Date().getDate()).padStart(2, "0")}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  useEffect(() => {
    api.get("/famous-birthdays", { params: { date: ddmm, days: 7, limit: 6 } }).then(async (r) => {
      const days = r.data.days;
      const names = [...days[0].people, ...days.slice(1).flatMap((d) => d.people).slice(0, 6)].map((p) => p.name);
      let portraits = {};
      try { portraits = (await api.get("/portraits", { params: { names: names.join("|") } })).data.portraits; } catch { /* initials fallback */ }
      setData({ ...r.data, days: days.map((d) => ({ ...d, people: d.people.map((p) => ({ ...p, portrait: portraits[p.name]?.image || null, wiki_description: portraits[p.name]?.description || null })) })) });
    }).catch(() => setData({ days: [], total: 0 }));
  }, [ddmm]);
  if (!data || data.total === 0) return null;
  const today = data.days[0];
  const upcoming = data.days.slice(1).flatMap((d) => d.people.map((p) => ({ ...p, date_label: d.label }))).slice(0, 6);
  if (hideToday && upcoming.length === 0) return null;
  return (
    <section id="famous-birthdays" data-testid="famous-birthdays-section" className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-gold" />
          <h2 className="text-2xl font-bold text-ink">{hideToday ? "Coming up this week" : "Famous birthdays"}</h2>
          <span data-testid="famous-birthdays-count" className="ml-1 text-[12px] font-semibold text-ink-muted">{hideToday ? `${upcoming.length} in the next 7 days` : `${data.total} this week`}</span>
        </div>
        <Link to="/birthdays#twins" data-testid="famous-birthdays-link" className="text-[13px] font-semibold text-gold hover:underline">Who shares your birthday? →</Link>
      </div>
      <div className={`grid grid-cols-1 gap-6 ${hideToday ? "" : "lg:grid-cols-2"}`}>
        {!hideToday && (
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-gold flex items-center gap-1"><Cake className="w-3.5 h-3.5" /> {isToday ? "Today" : "On this date"} · {today.label}</p>
          {today.people.length === 0 ? <p className="text-sm text-ink-muted">No famous birthdays recorded for {today.label} yet.</p> : (
            <ul className="space-y-2" data-testid="famous-birthdays-today">{today.people.map((p, i) => <Person key={p.id} p={p} today={isToday} testid={`famous-birthday-today-${i + 1}`} />)}</ul>
          )}
        </div>
        )}
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-ink-muted">Coming up {isToday ? "this week" : "in the following days"}</p>
          {upcoming.length === 0 ? <p className="text-sm text-ink-muted">Nothing recorded for the next six days.</p> : (
            <ul className="space-y-2" data-testid="famous-birthdays-upcoming">{upcoming.map((p, i) => <Person key={`${p.id}-${i}`} p={p} testid={`famous-birthday-upcoming-${i + 1}`} />)}</ul>
          )}
        </div>
      </div>
    </section>
  );
};
